import { inject, Injectable, InjectionToken } from '@angular/core';
import {
  endAt,
  equalTo,
  get,
  increment,
  orderByChild,
  orderByKey,
  query,
  ref,
  remove,
  runTransaction,
  set,
  startAt,
  update,
} from 'firebase/database';
import { HttpsCallable, httpsCallable } from 'firebase/functions';
import {
  Cortito,
  createCortito,
  DailyClicks,
  dayKey,
  deviceOf,
  Extras,
  firstDayShown,
  Origins,
  siteKey,
  writeTags,
} from '../model/cortito';
import { RESERVED_ALIASES } from '../app.routes';
import { FIREBASE_DB } from './firebase';
import { FIREBASE_FUNCTIONS } from './firebase';
import { passwordProof, saltBytes } from './password';

export type CreateResult = 'created' | 'taken';

/**
 * No `0`, `1`, `l` or `o`. An alias that nobody chose is going to be read off a
 * screen and typed somewhere else, and those four are where that goes wrong.
 * The same alphabet and length the API uses, in `functions/src/cortito.ts`.
 */
const ALPHABET = '23456789abcdefghijkmnpqrstuvwxyz';

export const GENERATED_LENGTH = 6;

/** How many names are tried before giving up on sorting one out. */
const ALIAS_TRIES = 5;

/** Thirty-two letters and two hundred fifty-six bytes: no letter comes up oftener. */
export function newAlias(
  random: (size: number) => Uint8Array = (size) => crypto.getRandomValues(new Uint8Array(size)),
): string {
  return Array.from(random(GENERATED_LENGTH), (byte) => ALPHABET[byte % ALPHABET.length]).join('');
}

type ProtectedRequest =
  | {
      action: 'create';
      alias: string;
      url: string;
      extras: Extras;
      salt: string;
      verifier: string;
    }
  | { action: 'unlock'; alias: string; verifier: string }
  | { action: 'destinations'; aliases: string[] }
  | { action: 'setDestination'; alias: string; url: string }
  | { action: 'remove'; alias: string }
  | { action: 'restore'; alias: string };

interface ProtectedResponse {
  outcome?: CreateResult;
  url?: string;
  destinations?: Record<string, string>;
}

export const PROTECTED_CORTITO = new InjectionToken<
  HttpsCallable<ProtectedRequest, ProtectedResponse>
>('PROTECTED_CORTITO', {
  providedIn: 'root',
  factory: () => httpsCallable(inject(FIREBASE_FUNCTIONS), 'protectedCortito'),
});

/**
 * `corti.to/<alias>+` asks to see where the link goes instead of going there,
 * the way bit.ly does it. The script inlined in `index.html` repeats the sign
 * because it runs long before this file exists.
 */
export const PREVIEW_SUFFIX = '+';

/** What the form asks for, in the order it asks: the three UTM everyone uses. */
export const UTM_FIELDS = ['source', 'medium', 'campaign'] as const;

export type Utm = Record<(typeof UTM_FIELDS)[number], string>;

/**
 * A destination the app will take: a host with a dot in it, a scheme or none,
 * and whatever path follows. The form asks it of a new cortito and the list asks
 * it again of one being sent somewhere else, so both hold a link to one shape.
 */
export const LONG_URL = /^(?:https?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&%'()*+,;=.]*$/;

export function isSelfReference(longUrl: string): boolean {
  return longUrl.includes('corti.to');
}

function withScheme(longUrl: string): string {
  return /^(f|ht)tps?:\/\//i.test(longUrl) ? longUrl : `http://${longUrl}`;
}

/**
 * Where the link lands, as a person would read it. A stored URL may have no
 * scheme — the form accepts `ejemplo.com/algo` — and `main.py` reads it the
 * same way for the card it hands to a chat app.
 */
export function destinationHost(longUrl: string): string {
  try {
    return new URL(withScheme(longUrl)).host || longUrl;
  } catch {
    return longUrl;
  }
}

/** An empty field is left out; one already in the link is replaced. */
export function withUtm(longUrl: string, utm: Partial<Utm>): string {
  const filled = UTM_FIELDS.map((field) => [field, utm[field]?.trim() ?? ''] as const).filter(
    ([, value]) => value,
  );
  if (!filled.length) {
    return longUrl;
  }

  const url = new URL(withScheme(longUrl));
  for (const [field, value] of filled) {
    url.searchParams.set(`utm_${field}`, value);
  }
  return url.toString();
}

/**
 * The optional fields as the form hands them over, none of them required. A
 * date on its own is read as the whole day: the cortito lasts until that day
 * ends where whoever set it lives. What was left empty is left out, so the
 * cortito is stored without the field at all.
 */
export function extrasFrom(fields: { expiresOn: string; maxClicks: string; tags: string }): Extras {
  const extras: Extras = {};

  const [year, month, day] = fields.expiresOn.split('-').map(Number);
  if (year && month && day) {
    extras.expiresAt = new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
  }
  const ceiling = Number(fields.maxClicks);
  if (Number.isInteger(ceiling) && ceiling > 0) {
    extras.maxClicks = ceiling;
  }
  const tags = writeTags(fields.tags.split(','));
  if (tags) {
    extras.tags = tags;
  }
  return extras;
}

@Injectable({ providedIn: 'root' })
export class LinkService {
  private readonly db = inject(FIREBASE_DB);
  private readonly protectedCortito = inject(PROTECTED_CORTITO);

  async find(alias: string): Promise<Cortito | null> {
    const snapshot = await get(ref(this.db, `urls/${alias}`));
    return snapshot.exists() ? (snapshot.val() as Cortito) : null;
  }

  /**
   * A name nobody chose: free, and not one the app keeps for itself. Empty when
   * a few tries found none, which with six letters out of thirty-two takes a
   * database that is nearly full.
   */
  async suggestAlias(): Promise<string> {
    for (let tries = 0; tries < ALIAS_TRIES; tries++) {
      const alias = newAlias();
      if (!RESERVED_ALIASES.includes(alias) && !(await this.find(alias))) {
        return alias;
      }
    }
    return '';
  }

  async listFor(userId: string): Promise<Cortito[]> {
    const snapshot = await get(
      query(ref(this.db, 'urls'), orderByChild('userId'), equalTo(userId)),
    );
    const links: Cortito[] = [];
    snapshot.forEach((child) => {
      links.push(child.val() as Cortito);
    });
    const protectedAliases = links.filter((link) => link.protected).map((link) => link.smallAlias);
    if (protectedAliases.length) {
      const { data } = await this.protectedCortito({
        action: 'destinations',
        aliases: protectedAliases,
      });
      for (const link of links) {
        if (link.protected) {
          link.longUrl = data.destinations?.[link.smallAlias];
        }
      }
    }
    return links;
  }

  async create(
    userId: string,
    longUrl: string,
    alias: string,
    extras: Extras = {},
    password = '',
  ): Promise<CreateResult> {
    if (await this.find(alias)) {
      return 'taken';
    }
    if (password) {
      const proof = await passwordProof(password);
      const { data } = await this.protectedCortito({
        action: 'create',
        alias,
        url: withScheme(longUrl),
        extras,
        ...proof,
      });
      return data.outcome ?? 'taken';
    }

    const link = createCortito(userId, withScheme(longUrl), alias, extras);
    await set(ref(this.db, `urls/${alias}`), link);
    // An alias that was used before may still have the previous cortito's days
    // under it, and taking the name over must not mean taking those over too.
    // Cleared here and not when a cortito is deleted, because the list offers to
    // undo a delete and the link comes back as it was.
    //
    // Only the owner may clear them, so a cortito made without an account keeps
    // whatever was there. Nothing leaks: without an owner nobody can read it
    // either, and the nightly sweep drops it within ninety days.
    if (userId) {
      await remove(ref(this.db, `stats/${alias}`)).catch(() => undefined);
    }
    await this.countCortitos(1);
    return 'created';
  }

  /** Brings an expired cortito back, by dropping whatever it was that ended it. */
  async clearLimits(alias: string): Promise<void> {
    await update(ref(this.db, `urls/${alias}`), { expiresAt: null, maxClicks: null });
  }

  /**
   * Puts a cortito on hold or takes it off hold again. Nothing else moves: the
   * alias stays taken while it is paused, so nobody can claim the name, and the
   * visits it has already served are still there when it comes back.
   *
   * A field that is gone rather than `false`, so a live cortito reads exactly
   * like every cortito made before this existed.
   */
  async setPaused(alias: string, paused: boolean): Promise<void> {
    await update(ref(this.db, `urls/${alias}`), { paused: paused || null });
  }

  /**
   * Sends a cortito somewhere else and touches nothing else: the alias stays, and
   * so do the visits it has already served. Answers with the destination as it
   * was stored, which is not always the one that was typed.
   */
  async setDestination(alias: string, longUrl: string): Promise<string> {
    const destination = withScheme(longUrl.trim());
    if ((await this.find(alias))?.protected) {
      const { data } = await this.protectedCortito({
        action: 'setDestination',
        alias,
        url: destination,
      });
      return data.url ?? destination;
    }
    await update(ref(this.db, `urls/${alias}`), { longUrl: destination });
    return destination;
  }

  /** The last label taken off a cortito leaves it with no field at all. */
  async setTags(alias: string, tags: string[]): Promise<void> {
    await update(ref(this.db, `urls/${alias}`), { tags: writeTags(tags) || null });
  }

  async restore(link: Cortito): Promise<void> {
    if (link.protected) {
      const { data } = await this.protectedCortito({ action: 'restore', alias: link.smallAlias });
      if (data.outcome !== 'created') {
        throw new Error('The protected cortito could not be restored.');
      }
      return;
    } else {
      await set(ref(this.db, `urls/${link.smallAlias}`), link);
    }
    await this.countCortitos(1);
  }

  async remove(alias: string): Promise<void> {
    if ((await this.find(alias))?.protected) {
      await this.protectedCortito({ action: 'remove', alias });
      return;
    }
    await remove(ref(this.db, `urls/${alias}`));
    await this.countCortitos(-1);
  }

  async unlock(alias: string, password: string, salt: string): Promise<string> {
    const { verifier } = await passwordProof(password, saltBytes(salt));
    const { data } = await this.protectedCortito({ action: 'unlock', alias, verifier });
    if (!data.url) {
      throw new Error('The protected destination was not returned.');
    }
    return data.url;
  }

  /**
   * The last thirty days of every alias asked for, each one read on its own.
   *
   * The days are stored per cortito and readable only by whoever owns it, so
   * there is no single query that brings them all: it is one read each, together,
   * and one that fails leaves that cortito without a chart instead of emptying
   * the list.
   */
  async dailyFor(aliases: string[]): Promise<Record<string, DailyClicks>> {
    const from = firstDayShown();
    const read = async (alias: string): Promise<[string, DailyClicks]> => {
      try {
        // Up to today and no further: `origin` sits under the same node, and a
        // word sorts after every date, so without the far end the tallies would
        // come back with the days and there is nothing here that would draw them.
        const snapshot = await get(
          query(ref(this.db, `stats/${alias}`), orderByKey(), startAt(from), endAt(dayKey())),
        );
        return [alias, (snapshot.val() as DailyClicks | null) ?? {}];
      } catch {
        return [alias, {}];
      }
    };
    return Object.fromEntries(await Promise.all(aliases.map(read)));
  }

  /**
   * Where one cortito's visits came from, read when its dialog is opened and not
   * before: the list draws thirty bars per cortito and none of this, so asking
   * for it with the rest would be a read per cortito nobody looks at.
   */
  async originsFor(alias: string): Promise<Origins> {
    const snapshot = await get(ref(this.db, `stats/${alias}/origin`));
    return (snapshot.val() as Origins | null) ?? {};
  }

  /**
   * The running total, the day the visit happened and where it came from, all
   * counted by the database itself and none of them awaited: the redirect must
   * not wait a whole round trip for a number nobody is looking at.
   *
   * Three writes and not one of several paths, which would be refused as a whole
   * if any one part of it were ever refused on its own. The total is what the app
   * has always counted, and neither a chart nor a map is worth costing it a
   * visit. They can drift, and they already do: every cortito made before this
   * had no history at all.
   */
  registerClick(alias: string): void {
    void update(ref(this.db, `urls/${alias}`), { clicks: increment(1) }).catch(() => {
      // A link that cannot be counted still has to resolve.
    });
    void update(ref(this.db, `stats/${alias}`), { [dayKey()]: increment(1) }).catch(() => {
      // Same for the day: it is the drawing that is lost, not the visit.
    });
    // The device and the site that sent it, which the browser knows about itself.
    // No country: only the server is told one, and a guess drawn on a map would
    // be worse than an empty map.
    void update(ref(this.db, `stats/${alias}/origin`), {
      [`device/${deviceOf(navigator.userAgent)}`]: increment(1),
      [`from/${siteKey(document.referrer)}`]: increment(1),
    }).catch(() => {
      // Same again: a visit is never lost over where it came from.
    });
  }

  private async countCortitos(delta: number): Promise<void> {
    try {
      await runTransaction(
        ref(this.db, 'cortitos'),
        (total: number | null) => (total ?? 0) + delta,
      );
    } catch {
      // The counter is cosmetic; never fail the operation because of it.
    }
  }
}
